import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import HeadCodeInjector from "@/components/HeadCodeInjector";
import RedirectHandler from "@/components/RedirectHandler";

// Pages
import LandingPage from "@/pages/LandingPage";
import ListingsPage from "@/pages/ListingsPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import LocationPage from "@/pages/LocationPage";
import ExplorePlacesPage from "@/pages/ExplorePlacesPage";
import PlaceDetailPage from "@/pages/PlaceDetailPage";
import BlogPage from "@/pages/BlogPage";
import ListYourHomestayPage from "@/pages/ListYourHomestayPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminHomestays from "@/pages/AdminHomestays";
import AdminReviews from "@/pages/AdminReviews";
import AdminInquiries from "@/pages/AdminInquiries";
import AdminLocations from "@/pages/AdminLocations";
import AdminSubLocations from "@/pages/AdminSubLocations";
import AdminPlaces from "@/pages/AdminPlaces";
import AdminBlog from "@/pages/AdminBlog";
import AdminAuthors from "@/pages/AdminAuthors";
import AdminFeaturedOn from "@/pages/AdminFeaturedOn";
import AdminSettings from "@/pages/AdminSettings";
import AdminDataMigration from "@/pages/AdminDataMigration";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminSEOLocations from "@/pages/AdminSEOLocations";
import AdminRedirects from "@/pages/AdminRedirects";
import AdminFeatures from "@/pages/AdminFeatures";
import AdminBulkHomestays from "@/pages/AdminBulkHomestays";
import AdminGoogleBulkUpload from "@/pages/AdminGoogleBulkUpload";
import AdminSEOImport from "@/pages/AdminSEOImport";
import AdminSeoCollections from "@/pages/AdminSeoCollections";
import AdminPlaceReviews from "@/pages/AdminPlaceReviews";
import AdminMedia from "@/pages/AdminMedia";
import LocationsPage from "@/pages/LocationsPage";
import SeoCollectionPage from "@/pages/SeoCollectionPage";
import RobotsTxt from "@/pages/RobotsTxt";
import SitemapXml from "@/pages/SitemapXml";
import LlmsTxt from "@/pages/LlmsTxt";
import SearchResultsPage from "@/pages/SearchResultsPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Context
import { AuthProvider } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Wrapper component to conditionally render extras
const AppContent = () => {
  const location = useLocation();
  const lastTracked = useRef("");
  
  // Track page views for analytics
  useEffect(() => {
    const key = location.pathname + location.search;
    if (key === lastTracked.current) return;
    if (location.pathname.startsWith("/admin")) return;
    lastTracked.current = key;
    
    const params = new URLSearchParams(location.search);
    fetch(`${BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: location.pathname,
        referrer: document.referrer || "",
        utm_source: params.get("utm_source") || "",
      }),
    }).catch(() => {});
  }, [location]);
  
  // Check if current route is SEO file
  const isSeoRoute = location.pathname === '/robots.txt' || location.pathname === '/sitemap.xml';
  
  return (
    <>
      {/* Only render HeadCodeInjector for non-SEO routes */}
      {!isSeoRoute && <HeadCodeInjector />}
      
      {/* Redirect Handler - Patch-based, doesn't affect meta/GA/GSC */}
      <RedirectHandler />
      
      <Routes>
        {/* SEO Files - Minimal rendering */}
        <Route path="/robots.txt" element={<RobotsTxt />} />
        <Route path="/sitemap.xml" element={<SitemapXml />} />
        <Route path="/llms.txt" element={<LlmsTxt />} />
        
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/search/:query" element={<SearchResultsPage />} />
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/locations" element={<LocationsPage />} />
        <Route path="/listings/:id" element={<ListingDetailPage />} />
        <Route path="/location/:locationId" element={<LocationPage />} />
        <Route path="/homestay/:slug" element={<ListingDetailPage />} />
        <Route path="/place/:slug" element={<PlaceDetailPage />} />
        <Route path="/places" element={<ExplorePlacesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/list-your-homestay" element={<ListYourHomestayPage />} />
        <Route path="/guide/:slug" element={<SeoCollectionPage />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/homestays" element={<AdminHomestays />} />
        <Route path="/admin/homestays/bulk-upload" element={<AdminBulkHomestays />} />
        <Route path="/admin/homestays/google-import" element={<AdminGoogleBulkUpload />} />
        <Route path="/admin/locations" element={<AdminLocations />} />
        <Route path="/admin/villages" element={<AdminSubLocations />} />
        <Route path="/admin/places" element={<AdminPlaces />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/authors" element={<AdminAuthors />} />
        <Route path="/admin/featured-on" element={<AdminFeaturedOn />} />
        <Route path="/admin/reviews" element={<AdminReviews />} />
        <Route path="/admin/inquiries" element={<AdminInquiries />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/migration" element={<AdminDataMigration />} />
        <Route path="/admin/seo" element={<AdminSEOLocations />} />
        <Route path="/admin/redirects" element={<AdminRedirects />} />
        <Route path="/admin/features" element={<AdminFeatures />} />
        <Route path="/admin/seo-import" element={<AdminSEOImport />} />
        <Route path="/admin/seo-collections" element={<AdminSeoCollections />} />
        <Route path="/admin/place-reviews" element={<AdminPlaceReviews />} />
        <Route path="/admin/media" element={<AdminMedia />} />
        
        {/* 404 - Must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      
      {/* Only render Toaster for non-SEO routes */}
      {!isSeoRoute && <Toaster position="top-right" richColors />}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
